import { useCurrentUser } from 'hooks/useCurrentUser';
import { GET_CUSTOM_WORKLISTS } from 'modules/Apollo/queries';
import { useCallback, useMemo } from 'react';
import { GetCustomWorklistsQuery, GetCustomWorklistsQueryVariables } from 'generated/graphql';
import { QueryHookOptions, useQuery } from '@apollo/client';
import { PrivateWorklist, PublicWorklist, WorklistSurface } from '../types';
import { isProvidedCannedWorklist } from '../utils/isProvidedCannedWorklist';

export const transformWorklistsToMap = <T extends { smid: string }>(
  customWorklists: ReadonlyArray<T>
) => {
  const worklists = new Map<string, T>();
  customWorklists.forEach((worklist) => {
    worklists.set(worklist.smid, worklist);
  });

  return worklists;
};

type UseCustomWorklists = {
  privateWorklists: Map<string, PrivateWorklist>;
  publicWorklists: Map<string, PublicWorklist>;
  allWorklists: Map<string, PrivateWorklist | PublicWorklist>;
  areCustomWorklistsLoading: boolean;
  hasPrivateWorklist: (worklistIds: string[]) => boolean;
  isPublicOrCombinedWorklist: (worklistIds: string[]) => boolean;
  isPrivateWorklist: (smid: string) => boolean;
  refetchWorklists: () => void;
  myQueueSmid: string | null;
};

export const useCustomWorklists = ({
  options = {},
  queryOptions = {},
}: {
  options?: {
    forceFetchQuery?: boolean;
  };
  queryOptions?: QueryHookOptions<GetCustomWorklistsQuery, GetCustomWorklistsQueryVariables>;
} = {}): UseCustomWorklists => {
  const { forceFetchQuery = false } = options;

  const { data: meData, loading: meLoading } = useCurrentUser({ context: queryOptions?.context });
  const userSmid = useMemo(() => meData?.me?.id ?? '', [meData?.me?.id]);
  const hasUserSmid = Boolean(userSmid);
  const shouldSkipQuery = !hasUserSmid || (!forceFetchQuery && Boolean(queryOptions.skip));

  const {
    data: customWorklistsData,
    loading: customWorklistLoading,
    refetch,
  } = useQuery<GetCustomWorklistsQuery, GetCustomWorklistsQueryVariables>(GET_CUSTOM_WORKLISTS, {
    variables: {
      userSmid: userSmid,
    },
    ...queryOptions,
    skip: shouldSkipQuery,
    context: queryOptions?.context,
  });

  const privateWorklists = useMemo(() => {
    return transformWorklistsToMap(customWorklistsData?.customWorklists?.privateWorklists ?? []);
  }, [customWorklistsData]);

  const publicWorklists = useMemo(() => {
    return transformWorklistsToMap(customWorklistsData?.customWorklists?.publicWorklists ?? []);
  }, [customWorklistsData]);

  const allWorklists = useMemo(() => {
    return new Map([...privateWorklists, ...publicWorklists]);
  }, [privateWorklists, publicWorklists]);

  const myQueueSmid = useMemo(() => {
    const privateWorklistsArray = Array.from(privateWorklists.values());
    const myQueueWorklist = privateWorklistsArray.find((worklist) =>
      isProvidedCannedWorklist({
        currentSurface: WorklistSurface.CUSTOM_WORKLIST,
        comparatorSurface: WorklistSurface.MY_QUEUE,
        customWorklistName: worklist.name,
      })
    );
    return myQueueWorklist?.smid ?? null;
  }, [privateWorklists]);

  const hasPrivateWorklist = useCallback(
    (worklistIds: string[]) => worklistIds?.some((id) => privateWorklists.has(id)) ?? false,
    [privateWorklists]
  );

  const isPrivateWorklist = useCallback(
    (smid: string) => privateWorklists.has(smid),
    [privateWorklists]
  );

  const isPublicOrCombinedWorklist = useCallback(
    (worklistIds: string[]) =>
      worklistIds && worklistIds.length > 1 ? true : !isPrivateWorklist(worklistIds[0]),
    [isPrivateWorklist]
  );

  return useMemo(
    () => ({
      privateWorklists,
      publicWorklists,
      areCustomWorklistsLoading: meLoading || customWorklistLoading,
      allWorklists,
      hasPrivateWorklist,
      isPublicOrCombinedWorklist,
      isPrivateWorklist,
      refetchWorklists: refetch,
      myQueueSmid,
    }),
    [
      privateWorklists,
      publicWorklists,
      meLoading,
      customWorklistLoading,
      allWorklists,
      hasPrivateWorklist,
      isPublicOrCombinedWorklist,
      isPrivateWorklist,
      refetch,
      myQueueSmid,
    ]
  );
};
